import type { HasChildren } from '@eppendorf/vnls-react-components';
import type { ReactNode } from 'react';

import type { AdditionalFeature } from '$shared/additional-features/types';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

interface AdditionalFeatureIsEnabledProps extends HasChildren {
  feature: AdditionalFeature;
  fallback?: ReactNode;
}

function parseAdditionalFeatures(value?: string): string[] {
  try {
    const features: unknown = JSON.parse(value ?? '[]');

    return Array.isArray(features) &&
      features.every((feature) => typeof feature === 'string')
      ? features
      : [];
  } catch {
    return [];
  }
}

export function useAdditionalFeatureIsEnabled(feature: AdditionalFeature): boolean {
  const { additionalFeatures } = useTokenAttributes(['additionalFeatures']);

  return parseAdditionalFeatures(additionalFeatures).includes(feature);
}

export function AdditionalFeatureIsEnabled({
  feature,
  fallback,
  children,
}: AdditionalFeatureIsEnabledProps): ReactNode {
  const isEnabled = useAdditionalFeatureIsEnabled(feature);

  return isEnabled ? children : (fallback ?? null);
}
